import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, MODAL_BREDDE, fontStr } from '@/config';
import type { ArsOppsummering } from '@/systems/arsoppsummering';
import { animateModalGlide } from '@/ui/modalGlide';
import { autoSpeed } from '@/systems/autoSpeed';

const SHEET_WIDTH = MODAL_BREDDE;
const GULL = 0xb8860b;

export class ArsoppsummeringModal {
  private layer?: Phaser.GameObjects.Container;
  private currentAar?: number;

  constructor(private scene: Phaser.Scene) {}

  open(oppsummering: ArsOppsummering): void {
    if (this.layer && this.currentAar === oppsummering.aar) return;
    this.close();
    this.currentAar = oppsummering.aar;
    autoSpeed.paus();

    this.layer = this.scene.add.container(0, 0).setDepth(220);
    animateModalGlide(this.scene, this.layer);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.82)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 120;
    const sheetH = GAME_HEIGHT - 240;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(4, COLORS.wine, 1);
    this.layer.add(sheet);

    const venstreBorder = this.scene.add
      .rectangle(sheetX, sheetY, 8, sheetH, GULL, 1)
      .setOrigin(0, 0);
    this.layer.add(venstreBorder);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - 24, sheetY + 16, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(56),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const padX = sheetX + 36;
    let y = sheetY + 40;

    const tittel = this.scene.add
      .text(padX, y, `År ${oppsummering.aar} oppsummert`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(52),
        color: '#862736',
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);
    y += tittel.height + 8;

    const undertittel = this.scene.add
      .text(
        padX,
        y,
        `Dag ${oppsummering.startDag} til ${oppsummering.sluttDag}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(undertittel);
    y += undertittel.height + 28;

    // KPI-cards
    const kpiW = (SHEET_WIDTH - 36 * 2 - 16 * 3) / 4;
    const kpiH = 110;
    this.tegnKpi(
      padX,
      y,
      kpiW,
      kpiH,
      'Total inntekt',
      `${oppsummering.totalInntekt.toLocaleString('nb-NO')} kr`,
      COLORS.forest,
    );
    this.tegnKpi(
      padX + (kpiW + 16),
      y,
      kpiW,
      kpiH,
      'Netto',
      `${oppsummering.netto.toLocaleString('nb-NO')} kr`,
      oppsummering.netto >= 0 ? COLORS.forest : COLORS.wine,
    );
    this.tegnKpi(
      padX + (kpiW + 16) * 2,
      y,
      kpiW,
      kpiH,
      'Utgivelser',
      String(oppsummering.utgivelser),
      COLORS.wine,
    );
    this.tegnKpi(
      padX + (kpiW + 16) * 3,
      y,
      kpiW,
      kpiH,
      'Bestselgere',
      String(oppsummering.bestselgere),
      GULL,
    );
    y += kpiH + 28;

    // Top-bok
    if (oppsummering.topBok) {
      const topBokLabel = this.scene.add
        .text(padX, y, 'Mest solgte bok', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#3a2e22',
          fontStyle: '600',
        })
        .setOrigin(0, 0);
      this.layer.add(topBokLabel);
      y += topBokLabel.height + 6;

      const topBokTxt = this.scene.add
        .text(
          padX,
          y,
          `«${oppsummering.topBok.tittel}» — ${oppsummering.topBok.salg.toLocaleString('nb-NO')} solgte`,
          {
            fontFamily: FONTS.display,
            fontSize: fontStr(28),
            color: '#1a1a1a',
            wordWrap: { width: SHEET_WIDTH - 72 },
          },
        )
        .setOrigin(0, 0);
      this.layer.add(topBokTxt);
      y += topBokTxt.height + 24;
    }

    // Brage og pris-vinster
    if (oppsummering.brageVinster > 0 || oppsummering.prisVinster > 0) {
      const prisLabel = this.scene.add
        .text(padX, y, 'Priser', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#3a2e22',
          fontStyle: '600',
        })
        .setOrigin(0, 0);
      this.layer.add(prisLabel);
      y += prisLabel.height + 6;

      if (oppsummering.brageVinster > 0) {
        const txt = this.scene.add
          .text(
            padX,
            y,
            `Brageprisen vunnet ${oppsummering.brageVinster} ` +
              `gang${oppsummering.brageVinster === 1 ? '' : 'er'}`,
            {
              fontFamily: FONTS.body,
              fontSize: fontStr(22),
              color: '#b8860b',
              fontStyle: '700',
            },
          )
          .setOrigin(0, 0);
        this.layer.add(txt);
        y += txt.height + 4;
      }
      if (oppsummering.prisVinster > 0) {
        const txt = this.scene.add
          .text(
            padX,
            y,
            `Andre priser: ${oppsummering.prisVinster}`,
            {
              fontFamily: FONTS.body,
              fontSize: fontStr(22),
              color: '#3d5a4d',
            },
          )
          .setOrigin(0, 0);
        this.layer.add(txt);
        y += txt.height + 4;
      }
      y += 18;
    }

    // Tone events
    if (oppsummering.toneEvents.vant + oppsummering.toneEvents.tapt > 0) {
      const toneLabel = this.scene.add
        .text(padX, y, 'Hendelser håndtert', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#3a2e22',
          fontStyle: '600',
        })
        .setOrigin(0, 0);
      this.layer.add(toneLabel);
      y += toneLabel.height + 6;

      const txt = this.scene.add
        .text(
          padX,
          y,
          `Gode utfall: ${oppsummering.toneEvents.vant} · Dårlige: ${oppsummering.toneEvents.tapt}`,
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(22),
            color: '#1a1a1a',
          },
        )
        .setOrigin(0, 0);
      this.layer.add(txt);
      y += txt.height + 24;
    }

    // Fortsett-knapp
    const knappW = 280;
    const knappH = 64;
    const knappX = sheetX + (SHEET_WIDTH - knappW) / 2;
    const knappY = sheetY + sheetH - knappH - 28;
    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Fortsett →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(26),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setFillStyle(0x6f1f2c));
    knappBg.on('pointerout', () => knappBg.setFillStyle(COLORS.wine));
    knappBg.on('pointerup', () => this.close());
  }

  private tegnKpi(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    verdi: string,
    farge: number,
  ): void {
    if (!this.layer) return;
    const bg = this.scene.add
      .rectangle(x, y, w, h, 0xfaf7f0, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, farge, 0.85);
    this.layer.add(bg);
    const lab = this.scene.add
      .text(x + 14, y + 12, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2e22',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    this.layer.add(lab);
    const verdiTxt = this.scene.add
      .text(x + 14, y + h - 14, verdi, {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: `#${farge.toString(16).padStart(6, '0')}`,
      })
      .setOrigin(0, 1);
    this.layer.add(verdiTxt);
  }

  close(): void {
    if (this.layer) {
      this.layer.destroy(true);
      this.layer = undefined;
    }
    this.currentAar = undefined;
  }
}
